'use client';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { motion } from 'framer-motion';

const ProjectsPage = () => {
  const navigate = useNavigate();
  const heroImage = `${process.env.PUBLIC_URL}/images/Hero/projectshero.webp`;

  const [projects] = useState([
    {
      title: 'Residence Moira, Goa',
      description:
        'A layered hillside residence where planted terraces, curved structural elements, and deep overhangs integrate architecture with landscape, allowing the house to step gently into its natural setting.',
      image: `${process.env.PUBLIC_URL}/images/projects/moira-goa/thumb.webp`,
      route: '/projects/moira-goa',
    },
    {
      title: 'Studio Sharan, New Delhi',
      description:
        'The studio is conceived as a calm, open working environment that balances focus, collaboration, and material clarity. The spatial organization is deliberately simple — a central conference zone anchored by a large working table, flanked by informal seating and pin-up surfaces that support dialogue, review, and iteration.',
      image: `${process.env.PUBLIC_URL}/images/projects/studiosharan/thumb.webp`,
      route: '/projects/studio-sharan',
    },
    {
      title: 'Residence 429, New Delhi',
      description:
        'This basement is conceived as a layered social environment rather than a residual lower floor. The space is organized as a sequence of intimate yet connected zones — lounge, bar, dining, and media — unified through a continuous material language and controlled lighting strategy.',
      image: `${process.env.PUBLIC_URL}/images/projects/429basement/thumb.webp`,
      route: '/projects/basement429-delhi',
    },
    {
      title: 'Siolim, Goa',
      description:
        'This residence is conceived as a layered tropical dwelling that balances openness with privacy, and lightness with mass. The architecture is composed as a series of horizontal concrete plates interlocked with deep shaded verandahs, terraces, and planted edges that respond to the coastal climate and the surrounding landscape.',
      image: `${process.env.PUBLIC_URL}/images/projects/siolim-goa/thumb.webp`,
      route: '/projects/siolim-goa',
    },
    {
      title: 'Residence 816, New Delhi',
      description:
        'This project is an urban infill residential building designed to negotiate between contemporary living requirements and the fine-grain scale of its historic neighborhood. The architecture inserts a new residential volume into an existing street fabric without overpowering it, responding carefully to height, rhythm, and material context.',
      image: `${process.env.PUBLIC_URL}/images/projects/safdarjung-delhi/thumb.webp`,
      route: '/projects/safdarjung-delhi',
    },
    
  ]);

  return (
    <div className="bg-white text-black min-h-screen font-sans">
      <Navbar />

      {/* Hero Section */}
      <section
        id="hero"
        className="relative h-[60vh] flex flex-col justify-center items-center overflow-hidden bg-black"
      >
        <img
          src={heroImage}
          alt="Projects Hero"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
      
        <motion.div
          className="relative z-10 text-center px-6 text-white"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
        >
          <h1 className="max-w-[92vw] text-center text-[clamp(2.5rem,10vw,7rem)] leading-[0.95] break-words font-heading font-light mb-4 tracking-wide">
            WORK
          </h1>
        </motion.div>
      </section>


      {/* Projects Grid */}
      <section className="py-24 px-8 md:px-20 bg-gray-50">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-20">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              className="group cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08, duration: 0.8 }}
              onClick={() => navigate(project.route)}
            >
              {/* Image Plate */}
              <div className="relative aspect-[16/9] overflow-hidden rounded-none group-hover:rounded-2xl transition-all duration-500 ease-out">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover scale-100 group-hover:scale-105 transition-transform duration-700 ease-out"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-500" />

                {/* Description only on hover */}
                <div className="absolute inset-0 flex items-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <p className="text-white/80 text-xs leading-relaxed max-w-[90%]">
                    {project.description}
                  </p>
                </div>
              </div>

              {/* Title always visible below */}
              <div className="mt-4">
                <h3 className="text-base font-medium tracking-wide text-gray-900">
                  {project.title}
                </h3>
                <div className="h-[1px] w-12 bg-gray-300 mt-2 group-hover:w-24 transition-all duration-500" />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectsPage;
